import {
  ConnectorTemplate,
  ConnectorFieldMapping,
  ConnectorDirection
} from "../../types"

/**
 * Predefined connector templates for common CRM integrations
 * These provide smart defaults that users can customize
 */

// Lead field mappings
export const ZAPIER_LEAD_MAPPING: ConnectorFieldMapping = [
  { incoming_key: "first_name", key: "first_name" },
  { incoming_key: "last_name", key: "last_name" },
  { incoming_key: "email", key: "email" },
  { incoming_key: "phone", key: "phone" },
  { incoming_key: "loan_purpose", key: "details.loan_purpose" },
  { incoming_key: "loan_type", key: "details.loan_type" },
  { incoming_key: "loan_amount", key: "details.loan_amount" },
  { incoming_key: "credit_score", key: "details.credit_score" },
  { incoming_key: "city", key: "details.location.city" },
  { incoming_key: "state", key: "details.location.state" },
  { incoming_key: "zipcode", key: "details.location.zipcode" },
  { incoming_key: "message", key: "details.message" }
]

export const GENERIC_LEAD_MAPPING: ConnectorFieldMapping = [
  { incoming_key: "firstname", key: "first_name" },
  { incoming_key: "lastname", key: "last_name" },
  { incoming_key: "email", key: "email" },
  { incoming_key: "phone", key: "phone" },
  { incoming_key: "creditscore", key: "details.credit_score" },
  { incoming_key: "downpayment", key: "details.down_payment" },
  { incoming_key: "listprice", key: "details.list_price" },
  { incoming_key: "loanamount", key: "details.loan_amount" },
  { incoming_key: "loanpurpose", key: "details.loan_purpose" },
  { incoming_key: "loantype", key: "details.loan_type" },
  { incoming_key: "loanterm", key: "details.loan_term" },
  { incoming_key: "propertytype", key: "details.property_type" },
  { incoming_key: "residencytype", key: "details.residency_type" },
  { incoming_key: "realtor", key: "details.realtor" },
  { incoming_key: "city", key: "details.location.city" },
  { incoming_key: "state", key: "details.location.state" },
  { incoming_key: "zipcode", key: "details.location.zipcode" },
  { incoming_key: "streetaddress", key: "details.location.street_address" },
  { incoming_key: "county", key: "details.location.county" },
  { incoming_key: "message", key: "details.message" }
]

// Subscription field mappings
export const ZAPIER_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = [
  { incoming_key: "phone", key: "phone" },
  { incoming_key: "email", key: "email" },
  { incoming_key: "loid", key: "loid" },
  { incoming_key: "first_name", key: "first_name" },
  { incoming_key: "last_name", key: "last_name" },
  { incoming_key: "loan_purpose", key: "quote_info.loan_purpose" },
  { incoming_key: "credit_score", key: "quote_info.credit_score" },
  { incoming_key: "property_type", key: "quote_info.property_type" },
  { incoming_key: "residency_type", key: "quote_info.residency_type" },
  { incoming_key: "list_price", key: "quote_info.list_price" },
  { incoming_key: "loan_amount", key: "quote_info.loan_amount" },
  { incoming_key: "loan_term", key: "quote_info.loan_term" },
  { incoming_key: "loan_type", key: "quote_info.loan_type" },
  { incoming_key: "state", key: "quote_info.location.state" },
  { incoming_key: "city", key: "quote_info.location.city" },
  { incoming_key: "zipcode", key: "quote_info.location.zipcode" },
  { incoming_key: "street_address", key: "quote_info.location.street_address" },
  { incoming_key: "county", key: "quote_info.location.county" }
]

// Pending subscription field mappings
export const ZAPIER_PENDING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = [
  { incoming_key: "phone", key: "phone" },
  { incoming_key: "email", key: "email" },
  { incoming_key: "loid", key: "loid" },
  { incoming_key: "first_name", key: "first_name" },
  { incoming_key: "last_name", key: "last_name" },
  { incoming_key: "trigger_rate", key: "trigger_rate" },
  { incoming_key: "loan_purpose", key: "quote_info.loan_purpose" },
  { incoming_key: "credit_score", key: "quote_info.credit_score" },
  { incoming_key: "property_type", key: "quote_info.property_type" },
  { incoming_key: "residency_type", key: "quote_info.residency_type" },
  { incoming_key: "list_price", key: "quote_info.list_price" },
  { incoming_key: "loan_amount", key: "quote_info.loan_amount" },
  { incoming_key: "loan_term", key: "quote_info.loan_term" },
  { incoming_key: "loan_type", key: "quote_info.loan_type" },
  { incoming_key: "state", key: "quote_info.location.state" },
  { incoming_key: "city", key: "quote_info.location.city" },
  { incoming_key: "zipcode", key: "quote_info.location.zipcode" },
  { incoming_key: "street_address", key: "quote_info.location.street_address" },
  { incoming_key: "county", key: "quote_info.location.county" }
]

// Own Up incoming lead mapping (for receiving leads from Own Up)
export const OWNUP_INCOMING_LEAD_MAPPING: ConnectorFieldMapping = [
  {
    incoming_key: "leadID",
    key: "details.source_lead_id"
  },
  {
    incoming_key: "leadCost",
    key: "details.lead_cost"
  },
  {
    incoming_key: "subsource",
    key: "details.subsource"
  },
  {
    incoming_key: "firstName",
    key: "first_name"
  },
  {
    incoming_key: "lastName",
    key: "last_name"
  },
  {
    incoming_key: "email",
    key: "email"
  },
  {
    incoming_key: "phoneNumber",
    key: "phone"
  },
  {
    incoming_key: "creditRating",
    key: "details.credit_score",
    values: {
      Excellent: 800,
      Good: 700,
      Fair: 650,
      Poor: 600,
      "Very Poor": 550,
      default: 650
    }
  },
  {
    incoming_key: "vaEligible",
    key: "details.va_eligible"
  },
  {
    incoming_key: "fthb",
    key: "details.first_time_buyer"
  },
  {
    incoming_key: "bankruptcy",
    key: "details.foreclosure_bankruptcy"
  },
  {
    incoming_key: "hasAgent",
    key: "details.has_agent"
  },
  {
    incoming_key: "hasCoborrower",
    key: "details.has_coborrower"
  },
  {
    incoming_key: "incomeType",
    key: "details.income_type",
    values: {
      Salary: "salary",
      Hourly: "hourly",
      Bonus: "bonus",
      Commission: "commission",
      Pension: "pension",
      SelfEmployed: "self_employed",
      NoIncome: "no_income",
      Other: "other",
      default: "salary"
    }
  },
  {
    incoming_key: "grossIncome",
    key: "details.household_income"
  },
  {
    incoming_key: "propertyStreet",
    key: "details.location.street_address"
  },
  {
    incoming_key: "propertyCity",
    key: "details.location.city"
  },
  {
    incoming_key: "propertyState",
    key: "details.location.state"
  },
  {
    incoming_key: "propertyCounty",
    key: "details.location.county"
  },
  {
    incoming_key: "propertyZip",
    key: "details.location.zipcode"
  },
  {
    incoming_key: "propertyValue",
    key: "details.list_price"
  },
  {
    incoming_key: "propertyType",
    key: "details.property_type",
    values: {
      SingleFamily: "single_family_home",
      MultiFamily: "multi_tenant_home",
      Condo: "condominium",
      Manufactured: "single_family_home",
      Townhome: "townhome",
      NewConstruction: "single_family_home",
      Mobile: "single_family_home",
      default: "single_family_home"
    }
  },
  {
    incoming_key: "propertyUse",
    key: "details.residency_type",
    values: {
      Primary: "primary_home",
      Secondary: "second_home",
      Investment: "rental_home",
      default: "primary_home"
    }
  },
  {
    incoming_key: "loanPurpose",
    key: "details.loan_purpose",
    values: {
      Purchase: "purchase",
      Refinance: "refinance",
      default: "purchase"
    }
  },
  {
    incoming_key: "loanAmount",
    key: "details.loan_amount"
  },
  {
    incoming_key: "hasAcceptedOffer",
    key: "details.has_accepted_offer"
  },
  {
    incoming_key: "cashOutAmount",
    key: "details.cashout_amount"
  },
  {
    incoming_key: "currentMortgageBalance",
    key: "details.current_mortgage_balance"
  },
  {
    incoming_key: "currentMortgageRate",
    key: "details.current_mortgage_rate"
  },
  {
    incoming_key: "jornayaLeadId",
    key: "details.jornaya_lead_id"
  },
  {
    incoming_key: "trustedFormCertificateUrl",
    key: "details.trusted_form_url"
  },
  {
    incoming_key: "source",
    key: "details.lead_source"
  },
  {
    incoming_key: "sourceId",
    key: "details.source_id"
  },
  {
    incoming_key: "anonymousId",
    key: "details.anonymous_id"
  },
  {
    incoming_key: "sourceChannel",
    key: "details.source_channel"
  },
  {
    incoming_key: "leadPricePaid",
    key: "details.lead_price_paid"
  },
  {
    incoming_key: "dateLeadReceived",
    key: "details.date_lead_received"
  },
  {
    incoming_key: "exclusive",
    key: "details.exclusive"
  },
  {
    incoming_key: "coborrowerEmail",
    key: "details.coborrower_email"
  },
  {
    incoming_key: "hasPreapproval",
    key: "details.has_preapproval"
  },
  {
    incoming_key: "questionnaireStartTime",
    key: "details.questionnaire_start_time"
  },
  {
    incoming_key: "questionnaireCompletionTime",
    key: "details.questionnaire_completion_time"
  },
  {
    incoming_key: "purchaseTimeline",
    key: "details.purchase_timeline_months"
  },
  {
    incoming_key: "closeDate",
    key: "details.close_date"
  },
  {
    incoming_key: "accountId",
    key: "details.account_id"
  },
  {
    incoming_key: "advisor",
    key: "details.advisor_email"
  },
  {
    incoming_key: "hubspotContactId",
    key: "details.hubspot_contact_id"
  },
  {
    incoming_key: "hubspotDealId",
    key: "details.hubspot_deal_id"
  },
  {
    incoming_key: "loanType",
    key: "details.loan_type",
    values: {
      Conventional: "conventional",
      "Homeready/Homepossible": "homeready",
      FHA: "fha",
      HELOC: "heloc",
      Reverse: "reverse",
      USDA: "usda",
      VA: "va",
      Jumbo: "jumbo",
      "Non QM": "non_qm",
      DPA: "dpa",
      Construction: "construction",
      Rehab: "rehab",
      Renovation: "renovation",
      default: "conventional"
    }
  },
  {
    incoming_key: "campaign",
    key: "details.campaign"
  },
  {
    incoming_key: "campaignId",
    key: "details.campaign_id"
  },
  {
    incoming_key: "comments",
    key: "details.message"
  },
  {
    incoming_key: "currentProductType",
    key: "details.current_product_type",
    values: {
      Fixed30: "fixed_30",
      Fixed20: "fixed_20",
      Fixed15: "fixed_15",
      Fixed10: "fixed_10",
      ARM10: "arm_10",
      ARM7: "arm_7",
      ARM5: "arm_5",
      Other: "other",
      default: "fixed_30"
    }
  },
  {
    incoming_key: "leadType",
    key: "details.lead_type",
    values: {
      "web-lead": "web_lead",
      "direct-web-lead": "direct_web_lead",
      "preapproval-lead": "preapproval_lead",
      "preapproval-lead-tier-2": "preapproval_lead_tier_2",
      "rate-quote-lead": "rate_quote_lead",
      "click-out-lead": "click_out_lead",
      "rate-table-lead": "rate_table_lead",
      "recapture-from-concierge-lead": "recapture_lead",
      default: "web_lead"
    }
  },
  {
    incoming_key: "ownOrRent",
    key: "details.own_or_rent",
    values: {
      Own: "own",
      Rent: "rent",
      default: "own"
    }
  },
  {
    incoming_key: "timeline",
    key: "details.timeline",
    values: {
      "0-3Months": "0_3_months",
      "3-6Months": "3_6_months",
      "6+Months": "6_plus_months",
      default: "3_6_months"
    }
  },
  {
    incoming_key: "questionnaireType",
    key: "details.questionnaire_type",
    values: {
      purchase: "purchase",
      preapproval: "preapproval",
      refinance: "refinance",
      "accepted-offer": "accepted_offer",
      default: "purchase"
    }
  },
  {
    incoming_key: "downPayment",
    key: "details.down_payment"
  },
  {
    incoming_key: "employmentStatus",
    key: "details.employment_status",
    values: {
      Employed: "employed",
      SelfEmployed: "self_employed",
      Retired: "retired",
      Unemployed: "unemployed",
      default: "employed"
    }
  },
  {
    incoming_key: "state",
    key: "details.shopping_state"
  }
]

// MRC incoming lead mapping (for receiving leads from Mortgage Research Center)
export const MRC_INCOMING_LEAD_MAPPING: ConnectorFieldMapping = [
  {
    incoming_key: "Internal_LeadID",
    key: "details.source_lead_id"
  },
  {
    incoming_key: "source",
    key: "details.lead_source"
  },
  {
    incoming_key: "lead_price_paid",
    key: "details.lead_cost"
  },
  { incoming_key: "first_name", key: "first_name" },
  { incoming_key: "last_name", key: "last_name" },
  { incoming_key: "phone", key: "phone" },
  {
    incoming_key: "phone_home",
    key: "details.phone_home"
  },
  {
    incoming_key: "phone_cell",
    key: "phone"
  },
  {
    incoming_key: "phone_work",
    key: "details.phone_work"
  },
  {
    incoming_key: "address",
    key: "details.current_address"
  },
  {
    incoming_key: "address2",
    key: "details.current_address2"
  },
  {
    incoming_key: "city",
    key: "details.current_city"
  },
  {
    incoming_key: "state",
    key: "details.current_state"
  },
  {
    incoming_key: "zip_code",
    key: "details.current_zipcode"
  },
  {
    incoming_key: "county",
    key: "details.current_county"
  },
  {
    incoming_key: "country",
    key: "details.country"
  },
  {
    incoming_key: "ip_address",
    key: "details.ip_address"
  },
  {
    incoming_key: "current_rate",
    key: "details.current_mortgage_rate"
  },
  {
    incoming_key: "living_situation",
    key: "details.living_situation",
    values: {
      own: "own",
      rent: "rent",
      other: "other",
      default: "own"
    }
  },
  {
    incoming_key: "military_status",
    key: "details.military_status",
    values: {
      active: "active",
      veteran: "veteran",
      reserves: "reserves",
      spouse: "spouse",
      none: "none",
      default: "none"
    }
  },
  {
    incoming_key: "credit_rating",
    key: "details.credit_score",
    values: {
      Excellent: 800,
      Good: 720,
      Fair: 660,
      Poor: 600,
      Bad: 550,
      default: 660
    }
  },
  {
    incoming_key: "email",
    key: "email"
  },
  {
    incoming_key: "bankruptcy",
    key: "details.foreclosure_bankruptcy",
    values: {
      N: "no",
      Y: "yes",
      "2year": "2_years",
      "5year": "5_years",
      "7year": "7_years",
      default: "no"
    }
  },
  {
    incoming_key: "leadid_token",
    key: "details.jornaya_lead_id"
  },
  {
    incoming_key: "loan_product",
    key: "details.loan_type",
    values: {
      va: "va",
      usda: "usda",
      fha: "fha",
      conventional: "conventional",
      default: "conventional"
    }
  },
  {
    incoming_key: "loan_purpose",
    key: "details.loan_purpose",
    values: {
      P: "purchase",
      R: "refinance",
      default: "purchase"
    }
  },
  {
    incoming_key: "military",
    key: "details.military_eligible",
    values: {
      N: "no",
      Y: "yes",
      default: "no"
    }
  },
  {
    incoming_key: "military_branch",
    key: "details.military_branch",
    values: {
      Army: "army",
      Marine: "marine",
      Navy: "navy",
      Air: "air_force",
      Coast: "coast_guard",
      National: "national_guard",
      Spouse: "spouse",
      Other: "other",
      Civilian: "civilian",
      default: "civilian"
    }
  },
  {
    incoming_key: "property_state",
    key: "details.location.state"
  },
  {
    incoming_key: "property_type",
    key: "details.property_type",
    values: {
      single: "single_family_home",
      condominium: "condominium",
      townhome: "townhome",
      multifamily: "multi_tenant_home",
      other: "single_family_home",
      manufactured: "single_family_home",
      "new construction": "single_family_home",
      default: "single_family_home"
    }
  },
  {
    incoming_key: "property_value",
    key: "details.list_price"
  },
  {
    incoming_key: "property_zip",
    key: "details.location.zipcode"
  },
  {
    incoming_key: "real_estate_agent",
    key: "details.has_agent",
    values: {
      N: "no",
      Y: "yes",
      default: "no"
    }
  },
  {
    incoming_key: "refi_balance",
    key: "details.current_mortgage_balance"
  },
  {
    incoming_key: "refi_value",
    key: "details.refi_property_value"
  },
  {
    incoming_key: "when_loan",
    key: "details.purchase_readiness",
    values: {
      "signed purchase agreement": "considering_loan_offers_now",
      "offer pending": "considering_loan_offers_now",
      "within a month": "thirty_days_out",
      "2 to 3 months": "two_to_four_months",
      "3 to 6 months": "two_to_four_months",
      "over 6 months": "researching_options",
      "no time frame": "researching_options",
      now: "considering_loan_offers_now",
      soon: "thirty_days_out",
      someday: "researching_options",
      default: "two_to_four_months"
    }
  },
  {
    incoming_key: "refi_reason",
    key: "details.refi_reason",
    values: {
      rate: "lower_rate",
      home_improvements: "home_improvements",
      payoff_debt: "payoff_debt",
      cashout: "cashout",
      consolidation: "consolidation",
      other: "other",
      lower_payments: "lower_payments",
      payoff_faster: "payoff_faster",
      default: "lower_rate"
    }
  },
  {
    incoming_key: "self_employed",
    key: "details.self_employed",
    values: {
      N: "no",
      Y: "yes",
      default: "no"
    }
  },
  {
    incoming_key: "purchase_first",
    key: "details.first_time_buyer",
    values: {
      N: "no",
      Y: "yes",
      default: "no"
    }
  },
  {
    incoming_key: "manufactured_own",
    key: "details.manufactured_land_ownership",
    values: {
      own: "own",
      rent: "rent",
      default: "own"
    }
  },
  {
    incoming_key: "down_payment_pct",
    key: "details.down_payment_percent"
  },
  {
    incoming_key: "property_url",
    key: "details.property_url"
  },
  {
    incoming_key: "gross_income",
    key: "details.household_income"
  },
  {
    incoming_key: "employment_status",
    key: "details.employment_status",
    values: {
      employed: "employed",
      "self employed": "self_employed",
      retired: "retired",
      "not employed": "unemployed",
      default: "employed"
    }
  },
  {
    incoming_key: "property_city",
    key: "details.location.city"
  },
  {
    incoming_key: "loan_term",
    key: "details.loan_term"
  },
  {
    incoming_key: "addr_city",
    key: "details.current_city"
  },
  {
    incoming_key: "addr_state",
    key: "details.current_state"
  },
  {
    incoming_key: "down_payment",
    key: "details.down_payment"
  },
  {
    incoming_key: "property_use",
    key: "details.residency_type",
    values: {
      primary: "primary_home",
      secondary: "second_home",
      investment: "rental_home",
      default: "primary_home"
    }
  },
  {
    incoming_key: "property_county",
    key: "details.location.county"
  },
  {
    incoming_key: "refi_current_loan_type",
    key: "details.current_loan_type"
  },
  {
    incoming_key: "cash_outamt",
    key: "details.cashout_amount"
  }
]

// Bankrate incoming lead mapping (for receiving leads from Bankrate)
export const BANKRATE_INCOMING_LEAD_MAPPING: ConnectorFieldMapping = [
  // Basic fields
  { incoming_key: "leadId", key: "details.source_lead_id" },
  { incoming_key: "leadcost", key: "details.lead_cost" },
  { incoming_key: "firstName", key: "first_name" },
  { incoming_key: "lastName", key: "last_name" },
  { incoming_key: "email", key: "email" },
  { incoming_key: "phone", key: "phone" },

  // Location
  { incoming_key: "zipCode", key: "details.location.zipcode" },
  { incoming_key: "propertyCity", key: "details.location.city" },
  { incoming_key: "propertyState", key: "details.location.state" },

  // Loan info with transformations
  {
    incoming_key: "loanType",
    key: "details.loan_purpose",
    values: {
      "Home Purchase": "purchase",
      "Home Refinance": "refinance",
      default: "purchase"
    }
  },
  { incoming_key: "loanAmount", key: "details.loan_amount" },

  // Property with transformations
  {
    incoming_key: "propertyType",
    key: "details.property_type",
    values: {
      "Single Family Home": "single_family_home",
      "Multi-Family Home": "multi_tenant_home",
      Condominium: "condominium",
      Townhome: "townhome",
      default: "single_family_home"
    }
  },
  {
    incoming_key: "propertyUse",
    key: "details.residency_type",
    values: {
      "Primary Home": "primary_home",
      "Secondary Home": "second_home",
      "Rental Property": "rental_home",
      default: "primary_home"
    }
  },

  // Credit - direct pass-through
  { incoming_key: "creditProfileAmount", key: "details.credit_score" },

  // Military/bankruptcy with transformations
  {
    incoming_key: "isMilitaryMember",
    key: "details.military_eligible",
    values: {
      true: "yes",
      false: "no",
      default: "no"
    }
  },
  {
    incoming_key: "hasFiledBankruptcy",
    key: "details.foreclosure_bankruptcy",
    values: {
      true: "yes",
      false: "no",
      default: "no"
    }
  },

  // Employment
  {
    incoming_key: "employmentStatus",
    key: "details.employment_status",
    values: {
      Employed: "employed",
      "Not Employed": "unemployed",
      "Self Employed": "self_employed",
      Military: "military",
      Other: "other",
      default: "employed"
    }
  },
  { incoming_key: "hasProofIncome", key: "details.has_proof_income" },

  // Product info
  { incoming_key: "productDesc", key: "details.product_description" },
  { incoming_key: "productRate", key: "details.product_rate" },
  { incoming_key: "productAPR", key: "details.product_apr" },
  { incoming_key: "points", key: "details.points" },
  { incoming_key: "leadSourceCampaign", key: "details.campaign" },

  // Purchase-specific fields
  {
    incoming_key: "isFirstPropertyPurchase",
    key: "details.first_time_buyer",
    values: {
      true: "yes",
      false: "no",
      default: "no"
    }
  },
  {
    incoming_key: "propertyPurchaseSituation",
    key: "details.purchase_readiness",
    values: {
      "Offer Accepted by Seller": "considering_loan_offers_now",
      "Making Offers": "considering_loan_offers_now",
      "Going to Open Houses": "thirty_days_out",
      "Researching Options": "researching_options",
      default: "researching_options"
    }
  },
  { incoming_key: "purchasePriceAmount", key: "details.list_price" },
  { incoming_key: "downPaymentAmount", key: "details.down_payment" },
  { incoming_key: "downPaymentPercent", key: "details.down_payment_percent" },
  { incoming_key: "rateTypeDesired", key: "details.rate_type" },
  { incoming_key: "grossAnnualIncomeAmount", key: "details.household_income" },
  {
    incoming_key: "isWorkingWithRealtor",
    key: "details.has_agent",
    values: {
      true: "yes",
      false: "no",
      default: "no"
    }
  },

  // Refinance-specific fields
  { incoming_key: "housePurchaseYear", key: "details.house_purchase_year" },
  { incoming_key: "propertyEstimatedValueAmount", key: "details.list_price" },
  {
    incoming_key: "firstMortgageBalanceAmount",
    key: "details.current_mortgage_balance"
  },
  { incoming_key: "firstMortgageRate", key: "details.current_mortgage_rate" },
  { incoming_key: "existingRateType", key: "details.existing_rate_type" },
  { incoming_key: "hasSecondMortgage", key: "details.has_second_mortgage" },
  { incoming_key: "additionalCashAmount", key: "details.cashout_amount" },
  { incoming_key: "averageMonthlyIncomeAmount", key: "details.monthly_income" },
  {
    incoming_key: "averageMonthlyExpensesAmount",
    key: "details.monthly_expenses"
  },
  { incoming_key: "hasLatePayments", key: "details.has_late_payments" },
  { incoming_key: "hasFHALoan", key: "details.has_fha_loan" }
]

// Bankrate incoming subscription mapping (for receiving subscriptions from Bankrate)
export const BANKRATE_INCOMING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = [
  // Basic fields
  { incoming_key: "firstName", key: "first_name" },
  { incoming_key: "lastName", key: "last_name" },
  { incoming_key: "email", key: "email" },
  { incoming_key: "phone", key: "phone" },

  // Location
  { incoming_key: "zipCode", key: "quote_info.location.zipcode" },
  { incoming_key: "propertyCity", key: "quote_info.location.city" },
  { incoming_key: "propertyState", key: "quote_info.location.state" },

  // Loan info with transformations
  {
    incoming_key: "loanType",
    key: "quote_info.loan_purpose",
    values: {
      "Home Purchase": "purchase",
      "Home Refinance": "refinance",
      default: "purchase"
    }
  },
  { incoming_key: "loanAmount", key: "quote_info.loan_amount" },

  // Property with transformations
  {
    incoming_key: "propertyType",
    key: "quote_info.property_type",
    values: {
      "Single Family Home": "single_family_home",
      "Multi-Family Home": "multi_tenant_home",
      Condominium: "condominium",
      Townhome: "townhome",
      default: "single_family_home"
    }
  },
  {
    incoming_key: "propertyUse",
    key: "quote_info.residency_type",
    values: {
      "Primary Home": "primary_home",
      "Secondary Home": "second_home",
      "Rental Property": "rental_home",
      default: "primary_home"
    }
  },

  // Credit - direct pass-through
  { incoming_key: "creditProfileAmount", key: "quote_info.credit_score" },

  // Purchase-specific fields
  { incoming_key: "purchasePriceAmount", key: "quote_info.list_price" }
]

// MRC incoming subscription mapping (for receiving subscriptions from Mortgage Research Center)
export const MRC_INCOMING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = [
  { incoming_key: "first_name", key: "first_name" },
  { incoming_key: "last_name", key: "last_name" },
  { incoming_key: "email", key: "email" },
  { incoming_key: "phone", key: "phone" },
  { incoming_key: "phone_cell", key: "phone" },
  { incoming_key: "loid", key: "loid" },
  {
    incoming_key: "credit_rating",
    key: "quote_info.credit_score",
    values: {
      Excellent: 800,
      Good: 720,
      Fair: 660,
      Poor: 600,
      Bad: 550,
      default: 660
    }
  },
  {
    incoming_key: "loan_product",
    key: "quote_info.loan_type",
    values: {
      va: "va",
      usda: "usda",
      fha: "fha",
      conventional: "conventional",
      default: "conventional"
    }
  },
  {
    incoming_key: "loan_purpose",
    key: "quote_info.loan_purpose",
    values: {
      P: "purchase",
      R: "refinance",
      default: "purchase"
    }
  },
  { incoming_key: "loan_amount", key: "quote_info.loan_amount" },
  { incoming_key: "loan_term", key: "quote_info.loan_term" },
  { incoming_key: "property_value", key: "quote_info.list_price" },
  {
    incoming_key: "property_type",
    key: "quote_info.property_type",
    values: {
      single: "single_family_home",
      condominium: "condominium",
      townhome: "townhome",
      multifamily: "multi_tenant_home",
      other: "single_family_home",
      manufactured: "single_family_home",
      "new construction": "single_family_home",
      default: "single_family_home"
    }
  },
  {
    incoming_key: "property_use",
    key: "quote_info.residency_type",
    values: {
      primary: "primary_home",
      secondary: "second_home",
      investment: "rental_home",
      default: "primary_home"
    }
  },
  { incoming_key: "property_state", key: "quote_info.location.state" },
  { incoming_key: "property_city", key: "quote_info.location.city" },
  { incoming_key: "property_zip", key: "quote_info.location.zipcode" },
  { incoming_key: "property_county", key: "quote_info.location.county" }
]

// Zillow incoming lead mapping (for receiving leads from Zillow)
export const ZILLOW_INCOMING_LEAD_MAPPING: ConnectorFieldMapping = [
  // Lead identification
  { incoming_key: "id", key: "details.source_lead_id" },
  { incoming_key: "price", key: "details.lead_cost" },
  { incoming_key: "source", key: "details.lead_source" },
  {
    incoming_key: "type",
    key: "details.lead_type",
    values: {
      simple: "web_lead",
      quote: "rate_quote_lead",
      longForm: "preapproval_lead",
      propertyPreapproval: "preapproval_lead",
      zhl: "web_lead",
      default: "web_lead"
    }
  },
  { incoming_key: "brand", key: "details.brand" },
  { incoming_key: "channel", key: "details.source_channel" },
  { incoming_key: "created", key: "details.date_lead_received" },

  // Sender/borrower basic info
  { incoming_key: "sender.firstName", key: "first_name" },
  { incoming_key: "sender.lastName", key: "last_name" },
  { incoming_key: "sender.emailAddress", key: "email" },
  { incoming_key: "sender.phoneNumber", key: "phone" },
  { incoming_key: "sender.middleName", key: "details.middle_name" },
  { incoming_key: "sender.suffix", key: "details.suffix" },

  // Recipient/lender info
  { incoming_key: "recipient.lenderId", key: "loid" },
  { incoming_key: "recipient.nmlsLicense", key: "details.nmls_license" },

  // Credit score - Zillow provides both low and high
  { incoming_key: "details.creditScoreLow", key: "details.credit_score" },
  { incoming_key: "details.creditScoreHigh", key: "details.credit_score_high" },
  {
    incoming_key: "details.creditScoreRange",
    key: "details.credit_score",
    values: {
      R_760_: 780,
      R_740_759: 749,
      R_720_739: 729,
      R_700_719: 709,
      R_680_699: 689,
      R_660_679: 669,
      R_640_659: 649,
      R_620_639: 629,
      R_600_619: 609,
      R_560_599: 579,
      R_300_559: 500,
      default: 650
    }
  },

  // Financial info
  { incoming_key: "details.annualIncome", key: "details.household_income" },
  { incoming_key: "details.monthlyDebts", key: "details.monthly_debts" },
  { incoming_key: "details.totalAssets", key: "details.total_assets" },
  { incoming_key: "details.debtToIncomePercent", key: "details.dti_ratio" },

  // Property location
  {
    incoming_key: "details.streetAddress",
    key: "details.location.street_address"
  },
  { incoming_key: "details.city", key: "details.location.city" },
  { incoming_key: "details.county", key: "details.location.county" },
  { incoming_key: "details.stateAbbreviation", key: "details.location.state" },
  { incoming_key: "details.zipCode", key: "details.location.zipcode" },
  {
    incoming_key: "details.propertyAddress",
    key: "details.location.street_address"
  },

  // Property info
  { incoming_key: "details.propertyValue", key: "details.list_price" },
  {
    incoming_key: "details.propertyType",
    key: "details.property_type",
    values: {
      SingleFamilyHome: "single_family_home",
      TownHouse: "townhome",
      CondoFourOrFewerStories: "condominium",
      CondoFiveOrMoreStories: "condominium",
      Cooperative: "condominium",
      MobileOrManufactured: "single_family_home",
      Modular: "single_family_home",
      Leasehold: "single_family_home",
      default: "single_family_home"
    }
  },
  {
    incoming_key: "details.propertyUse",
    key: "details.residency_type",
    values: {
      Primary: "primary_home",
      SecondaryOrVacation: "second_home",
      InvestmentOrRental: "rental_home",
      default: "primary_home"
    }
  },
  { incoming_key: "details.newConstruction", key: "details.new_construction" },
  { incoming_key: "details.yearPurchased", key: "details.year_purchased" },

  // Loan info
  {
    incoming_key: "details.loanPurpose",
    key: "details.loan_purpose",
    values: {
      Purchase: "purchase",
      Refinance: "refinance",
      HomeEquity: "refinance",
      Construction: "purchase",
      HELOC: "refinance",
      default: "purchase"
    }
  },
  { incoming_key: "details.loanAmount", key: "details.loan_amount" },
  { incoming_key: "details.downPayment", key: "details.down_payment" },
  { incoming_key: "details.loanToValuePercent", key: "details.ltv_ratio" },
  { incoming_key: "details.cashOut", key: "details.cashout_amount" },
  { incoming_key: "details.wantsCashOut", key: "details.wants_cashout" },
  {
    incoming_key: "details.currentBalance",
    key: "details.current_mortgage_balance"
  },

  // Loan program preferences
  { incoming_key: "details.desiredPrograms", key: "details.desired_programs" },
  {
    incoming_key: "details.acceptPrepaymentPenalty",
    key: "details.accept_prepayment_penalty"
  },
  {
    incoming_key: "details.closingTimelineDays",
    key: "details.closing_timeline_days"
  },

  // Borrower status
  { incoming_key: "details.firstTimeBuyer", key: "details.first_time_buyer" },
  { incoming_key: "details.homeOwner", key: "details.is_homeowner" },
  { incoming_key: "details.selfEmployed", key: "details.self_employed" },
  { incoming_key: "details.hasCoborrower", key: "details.has_coborrower" },
  { incoming_key: "details.sellingHome", key: "details.selling_home" },

  // Eligibility
  { incoming_key: "details.vaEligible", key: "details.va_eligible" },
  {
    incoming_key: "details.fhaStreamlineEligible",
    key: "details.fha_streamline_eligible"
  },
  { incoming_key: "details.harpEligible", key: "details.harp_eligible" },
  { incoming_key: "details.isDpaEligible", key: "details.dpa_eligible" },

  // VA-specific
  {
    incoming_key: "details.vaFirstTimeUser",
    key: "details.va_first_time_user"
  },
  { incoming_key: "details.vaHasDisability", key: "details.va_has_disability" },
  {
    incoming_key: "details.veteranType",
    key: "details.veteran_type",
    values: {
      RegularMilitary: "regular_military",
      Reserves: "reserves",
      NationalGuard: "national_guard",
      SpouseOfRegularMilitary: "military_spouse",
      SpouseOfReserveMilitary: "military_spouse",
      Army: "army",
      Navy: "navy",
      AirForce: "air_force",
      MarineCorps: "marine_corps",
      CoastGuard: "coast_guard",
      DepartmentOfDefense: "dod",
      default: "regular_military"
    }
  },

  // Bankruptcy/history
  {
    incoming_key: "details.hasBankruptcy",
    key: "details.foreclosure_bankruptcy"
  },
  { incoming_key: "details.hasForeclosure", key: "details.has_foreclosure" },
  {
    incoming_key: "details.hasSecondMortgage",
    key: "details.has_second_mortgage"
  },

  // Agent info
  { incoming_key: "details.hasAgent", key: "details.has_agent" },
  { incoming_key: "details.agentFirstName", key: "details.agent_first_name" },
  { incoming_key: "details.agentLastName", key: "details.agent_last_name" },
  {
    incoming_key: "details.agentBusinessName",
    key: "details.agent_business_name"
  },
  { incoming_key: "details.agentEmailAddress", key: "details.agent_email" },
  { incoming_key: "details.agentPhoneNumber", key: "details.agent_phone" },
  { incoming_key: "details.agentId", key: "details.agent_id" },

  // Other
  { incoming_key: "details.message", key: "details.message" },
  {
    incoming_key: "details.preferredLanguage",
    key: "details.preferred_language",
    values: {
      English: "english",
      Spanish: "spanish",
      Mandarin: "mandarin",
      Cantonese: "cantonese",
      Vietnamese: "vietnamese",
      Korean: "korean",
      Japanese: "japanese",
      Filipino: "filipino",
      Arabic: "arabic",
      French: "french",
      German: "german",
      Portuguese: "portuguese",
      Russian: "russian",
      Hindi: "hindi",
      default: "english"
    }
  },
  {
    incoming_key: "details.preferredContactTime",
    key: "details.preferred_contact_time"
  },
  {
    incoming_key: "details.originalContactTime",
    key: "details.original_contact_time"
  },
  {
    incoming_key: "details.journeyStage",
    key: "details.purchase_readiness",
    values: {
      LookingAtListings: "researching_options",
      TouringHomes: "thirty_days_out",
      MakingOffer: "considering_loan_offers_now",
      UnderContract: "considering_loan_offers_now",
      EstimatedAffordability: "researching_options",
      PreapprovedOrPrequalified: "thirty_days_out",
      MakingOfferOrUnderContract: "considering_loan_offers_now",
      default: "researching_options"
    }
  },
  {
    incoming_key: "details.currentBacker",
    key: "details.current_backer",
    values: {
      FannieMae: "fannie_mae",
      FreddieMac: "freddie_mac",
      FHA: "fha",
      default: "fannie_mae"
    }
  },
  { incoming_key: "details.quoteId", key: "details.quote_id" },
  { incoming_key: "details.requestId", key: "details.request_id" }
]

// LendingTree incoming lead mapping (for receiving leads from LendingTree)
export const LENDINGTREE_INCOMING_LEAD_MAPPING: ConnectorFieldMapping = [
  // Lead identification
  { incoming_key: "lead.leadExternalId", key: "details.source_lead_id" },
  {
    incoming_key: "lead.vertical",
    key: "details.loan_purpose",
    values: {
      "MORTGAGE.PURCHASE": "purchase",
      "MORTGAGE.REFINANCE": "refinance",
      HOMEEQUITY: "refinance",
      default: "purchase"
    }
  },
  { incoming_key: "lead.requestType", key: "details.lead_type" },

  // Consumer contact info
  { incoming_key: "lead.consumer.contact.firstName", key: "first_name" },
  { incoming_key: "lead.consumer.contact.lastName", key: "last_name" },
  { incoming_key: "lead.consumer.contact.emailAddress", key: "email" },
  { incoming_key: "lead.consumer.contact.primaryPhone", key: "phone" },

  // Consumer address (initial values, may be overwritten by property-specific address below)
  {
    incoming_key: "lead.consumer.contact.streetAddress",
    key: "details.location.street_address"
  },
  { incoming_key: "lead.consumer.contact.city", key: "details.location.city" },
  {
    incoming_key: "lead.consumer.contact.state",
    key: "details.location.state"
  },
  {
    incoming_key: "lead.consumer.contact.zip",
    key: "details.location.zipcode"
  },

  // Consumer profile
  {
    incoming_key: "lead.consumer.profile.isMilitary",
    key: "details.military_eligible",
    values: {
      true: "yes",
      false: "no",
      default: "no"
    }
  },
  {
    incoming_key: "lead.consumer.profile.employmentStatus",
    key: "details.employment_status",
    values: {
      EMPLOYED: "employed",
      SELFEMPLOYED: "self_employed",
      RETIRED: "retired",
      OTHER: "other",
      default: "employed"
    }
  },

  // Credit - selfCreditRating first (enum fallback), then assignedCredit overwrites with numeric score
  {
    incoming_key: "lead.consumer.profile.selfCreditRating",
    key: "details.credit_score",
    values: {
      EXCELLENT: 800,
      GOOD: 700,
      FAIR: 650,
      POOR: 600,
      default: 650
    }
  },
  {
    incoming_key: "lead.consumer.profile.assignedCredit",
    key: "details.credit_score"
  },

  // LendingTree metadata
  {
    incoming_key: "lead.lendingTreeInformation.partnerExternalId",
    key: "details.partner_external_id"
  },
  {
    incoming_key: "lead.lendingTreeInformation.partnerName",
    key: "details.partner_name"
  },
  {
    incoming_key: "lead.lendingTreeInformation.filterName",
    key: "details.filter_name"
  },
  {
    incoming_key: "lead.lendingTreeInformation.filterRoutingId",
    key: "details.filter_routing_id"
  },

  // === Purchase fields ===
  {
    incoming_key: "lead.mortgagePurchase.loanAmount",
    key: "details.loan_amount"
  },
  {
    incoming_key: "lead.mortgagePurchase.propertyPurchasePrice",
    key: "details.list_price"
  },
  {
    incoming_key: "lead.mortgagePurchase.downPayment",
    key: "details.down_payment"
  },
  {
    incoming_key: "lead.mortgagePurchase.workingWithAgent",
    key: "details.has_agent",
    values: {
      true: "yes",
      false: "no",
      default: "no"
    }
  },
  {
    incoming_key: "lead.mortgagePurchase.property.type",
    key: "details.property_type",
    values: {
      SINGLEFAMDET: "single_family_home",
      SINGLEFAMATT: "single_family_home",
      MULTIFAMILY: "multi_tenant_home",
      CONDO: "condominium",
      TOWNHOME: "townhome",
      default: "single_family_home"
    }
  },
  {
    incoming_key: "lead.mortgagePurchase.property.use",
    key: "details.residency_type",
    values: {
      OWNEROCCUPIED: "primary_home",
      SECONDARYVAC: "second_home",
      INVESTMENTRENTAL: "rental_home",
      default: "primary_home"
    }
  },
  {
    incoming_key: "lead.mortgagePurchase.property.city",
    key: "details.location.city"
  },
  {
    incoming_key: "lead.mortgagePurchase.property.state",
    key: "details.location.state"
  },
  {
    incoming_key: "lead.mortgagePurchase.property.zip",
    key: "details.location.zipcode"
  },
  {
    incoming_key: "lead.mortgagePurchase.property.county",
    key: "details.location.county"
  },

  // === Refinance fields ===
  {
    incoming_key: "lead.mortgageRefinance.loanAmount",
    key: "details.loan_amount"
  },
  {
    incoming_key: "lead.mortgageRefinance.propertyValue",
    key: "details.list_price"
  },
  {
    incoming_key: "lead.mortgageRefinance.cashOut",
    key: "details.cashout_amount"
  },
  {
    incoming_key: "lead.mortgageRefinance.firstMortgageBalance",
    key: "details.current_mortgage_balance"
  },
  {
    incoming_key: "lead.mortgageRefinance.property.type",
    key: "details.property_type",
    values: {
      SINGLEFAMDET: "single_family_home",
      SINGLEFAMATT: "single_family_home",
      MULTIFAMILY: "multi_tenant_home",
      CONDO: "condominium",
      TOWNHOME: "townhome",
      default: "single_family_home"
    }
  },
  {
    incoming_key: "lead.mortgageRefinance.property.use",
    key: "details.residency_type",
    values: {
      OWNEROCCUPIED: "primary_home",
      SECONDARYVAC: "second_home",
      INVESTMENTRENTAL: "rental_home",
      default: "primary_home"
    }
  },
  {
    incoming_key: "lead.mortgageRefinance.property.city",
    key: "details.location.city"
  },
  {
    incoming_key: "lead.mortgageRefinance.property.state",
    key: "details.location.state"
  },
  {
    incoming_key: "lead.mortgageRefinance.property.zip",
    key: "details.location.zipcode"
  },
  {
    incoming_key: "lead.mortgageRefinance.property.county",
    key: "details.location.county"
  },

  // === Home equity fields ===
  { incoming_key: "lead.homeEquity.loanAmount", key: "details.loan_amount" },
  { incoming_key: "lead.homeEquity.propertyValue", key: "details.list_price" },
  {
    incoming_key: "lead.homeEquity.firstMortgageBalance",
    key: "details.current_mortgage_balance"
  },
  {
    incoming_key: "lead.homeEquity.secondMortgageBalance",
    key: "details.second_mortgage_balance"
  },
  {
    incoming_key: "lead.homeEquity.property.type",
    key: "details.property_type",
    values: {
      SINGLEFAMDET: "single_family_home",
      SINGLEFAMATT: "single_family_home",
      MULTIFAMILY: "multi_tenant_home",
      CONDO: "condominium",
      TOWNHOME: "townhome",
      default: "single_family_home"
    }
  },
  {
    incoming_key: "lead.homeEquity.property.use",
    key: "details.residency_type",
    values: {
      OWNEROCCUPIED: "primary_home",
      SECONDARYVAC: "second_home",
      INVESTMENTRENTAL: "rental_home",
      default: "primary_home"
    }
  },
  {
    incoming_key: "lead.homeEquity.property.city",
    key: "details.location.city"
  },
  {
    incoming_key: "lead.homeEquity.property.state",
    key: "details.location.state"
  },
  {
    incoming_key: "lead.homeEquity.property.zip",
    key: "details.location.zipcode"
  },
  {
    incoming_key: "lead.homeEquity.property.county",
    key: "details.location.county"
  }
]

// LendingTree incoming subscription mapping (for receiving subscriptions from LendingTree)
export const LENDINGTREE_INCOMING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping =
  [
    // Consumer contact info
    { incoming_key: "lead.consumer.contact.firstName", key: "first_name" },
    { incoming_key: "lead.consumer.contact.lastName", key: "last_name" },
    { incoming_key: "lead.consumer.contact.emailAddress", key: "email" },
    { incoming_key: "lead.consumer.contact.primaryPhone", key: "phone" },

    // Consumer address (initial values, may be overwritten by property-specific address below)
    {
      incoming_key: "lead.consumer.contact.city",
      key: "quote_info.location.city"
    },
    {
      incoming_key: "lead.consumer.contact.state",
      key: "quote_info.location.state"
    },
    {
      incoming_key: "lead.consumer.contact.zip",
      key: "quote_info.location.zipcode"
    },

    // Loan purpose from vertical
    {
      incoming_key: "lead.vertical",
      key: "quote_info.loan_purpose",
      values: {
        "MORTGAGE.PURCHASE": "purchase",
        "MORTGAGE.REFINANCE": "refinance",
        HOMEEQUITY: "refinance",
        default: "purchase"
      }
    },

    // Credit - selfCreditRating first (enum fallback), then assignedCredit overwrites with numeric score
    {
      incoming_key: "lead.consumer.profile.selfCreditRating",
      key: "quote_info.credit_score",
      values: {
        EXCELLENT: 800,
        GOOD: 700,
        FAIR: 650,
        POOR: 600,
        default: 650
      }
    },
    {
      incoming_key: "lead.consumer.profile.assignedCredit",
      key: "quote_info.credit_score"
    },

    // === Purchase fields ===
    {
      incoming_key: "lead.mortgagePurchase.loanAmount",
      key: "quote_info.loan_amount"
    },
    {
      incoming_key: "lead.mortgagePurchase.propertyPurchasePrice",
      key: "quote_info.list_price"
    },
    {
      incoming_key: "lead.mortgagePurchase.downPayment",
      key: "quote_info.down_payment"
    },
    {
      incoming_key: "lead.mortgagePurchase.property.type",
      key: "quote_info.property_type",
      values: {
        SINGLEFAMDET: "single_family_home",
        SINGLEFAMATT: "single_family_home",
        MULTIFAMILY: "multi_tenant_home",
        CONDO: "condominium",
        TOWNHOME: "townhome",
        default: "single_family_home"
      }
    },
    {
      incoming_key: "lead.mortgagePurchase.property.use",
      key: "quote_info.residency_type",
      values: {
        OWNEROCCUPIED: "primary_home",
        SECONDARYVAC: "second_home",
        INVESTMENTRENTAL: "rental_home",
        default: "primary_home"
      }
    },
    {
      incoming_key: "lead.mortgagePurchase.property.city",
      key: "quote_info.location.city"
    },
    {
      incoming_key: "lead.mortgagePurchase.property.state",
      key: "quote_info.location.state"
    },
    {
      incoming_key: "lead.mortgagePurchase.property.zip",
      key: "quote_info.location.zipcode"
    },
    {
      incoming_key: "lead.mortgagePurchase.property.county",
      key: "quote_info.location.county"
    },

    // === Refinance fields ===
    {
      incoming_key: "lead.mortgageRefinance.loanAmount",
      key: "quote_info.loan_amount"
    },
    {
      incoming_key: "lead.mortgageRefinance.propertyValue",
      key: "quote_info.list_price"
    },
    {
      incoming_key: "lead.mortgageRefinance.cashOut",
      key: "quote_info.cashout_amount"
    },
    {
      incoming_key: "lead.mortgageRefinance.firstMortgageBalance",
      key: "quote_info.current_mortgage_balance"
    },
    {
      incoming_key: "lead.mortgageRefinance.property.type",
      key: "quote_info.property_type",
      values: {
        SINGLEFAMDET: "single_family_home",
        SINGLEFAMATT: "single_family_home",
        MULTIFAMILY: "multi_tenant_home",
        CONDO: "condominium",
        TOWNHOME: "townhome",
        default: "single_family_home"
      }
    },
    {
      incoming_key: "lead.mortgageRefinance.property.use",
      key: "quote_info.residency_type",
      values: {
        OWNEROCCUPIED: "primary_home",
        SECONDARYVAC: "second_home",
        INVESTMENTRENTAL: "rental_home",
        default: "primary_home"
      }
    },
    {
      incoming_key: "lead.mortgageRefinance.property.city",
      key: "quote_info.location.city"
    },
    {
      incoming_key: "lead.mortgageRefinance.property.state",
      key: "quote_info.location.state"
    },
    {
      incoming_key: "lead.mortgageRefinance.property.zip",
      key: "quote_info.location.zipcode"
    },
    {
      incoming_key: "lead.mortgageRefinance.property.county",
      key: "quote_info.location.county"
    },

    // === Home equity fields ===
    {
      incoming_key: "lead.homeEquity.loanAmount",
      key: "quote_info.loan_amount"
    },
    {
      incoming_key: "lead.homeEquity.propertyValue",
      key: "quote_info.list_price"
    },
    {
      incoming_key: "lead.homeEquity.firstMortgageBalance",
      key: "quote_info.current_mortgage_balance"
    },
    {
      incoming_key: "lead.homeEquity.secondMortgageBalance",
      key: "quote_info.second_mortgage_balance"
    },
    {
      incoming_key: "lead.homeEquity.property.type",
      key: "quote_info.property_type",
      values: {
        SINGLEFAMDET: "single_family_home",
        SINGLEFAMATT: "single_family_home",
        MULTIFAMILY: "multi_tenant_home",
        CONDO: "condominium",
        TOWNHOME: "townhome",
        default: "single_family_home"
      }
    },
    {
      incoming_key: "lead.homeEquity.property.use",
      key: "quote_info.residency_type",
      values: {
        OWNEROCCUPIED: "primary_home",
        SECONDARYVAC: "second_home",
        INVESTMENTRENTAL: "rental_home",
        default: "primary_home"
      }
    },
    {
      incoming_key: "lead.homeEquity.property.city",
      key: "quote_info.location.city"
    },
    {
      incoming_key: "lead.homeEquity.property.state",
      key: "quote_info.location.state"
    },
    {
      incoming_key: "lead.homeEquity.property.zip",
      key: "quote_info.location.zipcode"
    },
    {
      incoming_key: "lead.homeEquity.property.county",
      key: "quote_info.location.county"
    }
  ]

// Zillow incoming subscription mapping (for receiving subscriptions from Zillow)
export const ZILLOW_INCOMING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = [
  // Sender/borrower basic info
  { incoming_key: "sender.firstName", key: "first_name" },
  { incoming_key: "sender.lastName", key: "last_name" },
  { incoming_key: "sender.emailAddress", key: "email" },
  { incoming_key: "sender.phoneNumber", key: "phone" },

  // Recipient/lender info
  { incoming_key: "recipient.lenderId", key: "loid" },

  // Credit score
  { incoming_key: "details.creditScoreLow", key: "quote_info.credit_score" },
  {
    incoming_key: "details.creditScoreHigh",
    key: "quote_info.credit_score_high"
  },
  {
    incoming_key: "details.creditScoreRange",
    key: "quote_info.credit_score",
    values: {
      R_760_: 780,
      R_740_759: 749,
      R_720_739: 729,
      R_700_719: 709,
      R_680_699: 689,
      R_660_679: 669,
      R_640_659: 649,
      R_620_639: 629,
      R_600_619: 609,
      R_560_599: 579,
      R_300_559: 500,
      default: 650
    }
  },

  // Property location
  {
    incoming_key: "details.streetAddress",
    key: "quote_info.location.street_address"
  },
  { incoming_key: "details.city", key: "quote_info.location.city" },
  { incoming_key: "details.county", key: "quote_info.location.county" },
  {
    incoming_key: "details.stateAbbreviation",
    key: "quote_info.location.state"
  },
  { incoming_key: "details.zipCode", key: "quote_info.location.zipcode" },
  {
    incoming_key: "details.propertyAddress",
    key: "quote_info.location.street_address"
  },

  // Property info
  { incoming_key: "details.propertyValue", key: "quote_info.list_price" },
  {
    incoming_key: "details.propertyType",
    key: "quote_info.property_type",
    values: {
      SingleFamilyHome: "single_family_home",
      TownHouse: "townhome",
      CondoFourOrFewerStories: "condominium",
      CondoFiveOrMoreStories: "condominium",
      Cooperative: "condominium",
      MobileOrManufactured: "single_family_home",
      Modular: "single_family_home",
      Leasehold: "single_family_home",
      default: "single_family_home"
    }
  },
  {
    incoming_key: "details.propertyUse",
    key: "quote_info.residency_type",
    values: {
      Primary: "primary_home",
      SecondaryOrVacation: "second_home",
      InvestmentOrRental: "rental_home",
      default: "primary_home"
    }
  },

  // Loan info
  {
    incoming_key: "details.loanPurpose",
    key: "quote_info.loan_purpose",
    values: {
      Purchase: "purchase",
      Refinance: "refinance",
      HomeEquity: "refinance",
      Construction: "purchase",
      HELOC: "refinance",
      default: "purchase"
    }
  },
  { incoming_key: "details.loanAmount", key: "quote_info.loan_amount" },
  { incoming_key: "details.downPayment", key: "quote_info.down_payment" },
  { incoming_key: "details.loanToValuePercent", key: "quote_info.ltv_ratio" },
  { incoming_key: "details.cashOut", key: "quote_info.cashout_amount" },
  {
    incoming_key: "details.currentBalance",
    key: "quote_info.current_mortgage_balance"
  },

  // Borrower status
  {
    incoming_key: "details.firstTimeBuyer",
    key: "quote_info.first_time_buyer"
  },
  { incoming_key: "details.vaEligible", key: "quote_info.va_eligible" },
  {
    incoming_key: "details.fhaStreamlineEligible",
    key: "quote_info.fha_streamline_eligible"
  },
  { incoming_key: "details.harpEligible", key: "quote_info.harp_eligible" }
]

// Own Up incoming subscription mapping (for receiving subscriptions from Own Up)
export const OWNUP_INCOMING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = [
  { incoming_key: "firstName", key: "first_name" },
  { incoming_key: "lastName", key: "last_name" },
  { incoming_key: "email", key: "email" },
  { incoming_key: "phoneNumber", key: "phone" },
  { incoming_key: "loid", key: "loid" },
  {
    incoming_key: "creditRating",
    key: "quote_info.credit_score",
    values: {
      Excellent: 800,
      Good: 700,
      Fair: 650,
      Poor: 600,
      "Very Poor": 550,
      default: 650
    }
  },
  { incoming_key: "vaEligible", key: "quote_info.va_eligible" },
  { incoming_key: "fthb", key: "quote_info.first_time_buyer" },
  { incoming_key: "propertyStreet", key: "quote_info.location.street_address" },
  { incoming_key: "propertyCity", key: "quote_info.location.city" },
  { incoming_key: "propertyState", key: "quote_info.location.state" },
  { incoming_key: "propertyCounty", key: "quote_info.location.county" },
  { incoming_key: "propertyZip", key: "quote_info.location.zipcode" },
  { incoming_key: "propertyValue", key: "quote_info.list_price" },
  {
    incoming_key: "propertyType",
    key: "quote_info.property_type",
    values: {
      SingleFamily: "single_family_home",
      MultiFamily: "multi_tenant_home",
      Condo: "condominium",
      Manufactured: "single_family_home",
      Townhome: "townhome",
      NewConstruction: "single_family_home",
      Mobile: "single_family_home",
      default: "single_family_home"
    }
  },
  {
    incoming_key: "propertyUse",
    key: "quote_info.residency_type",
    values: {
      Primary: "primary_home",
      Secondary: "second_home",
      Investment: "rental_home",
      default: "primary_home"
    }
  },
  {
    incoming_key: "loanPurpose",
    key: "quote_info.loan_purpose",
    values: {
      Purchase: "purchase",
      Refinance: "refinance",
      default: "purchase"
    }
  },
  { incoming_key: "loanAmount", key: "quote_info.loan_amount" },
  {
    incoming_key: "loanType",
    key: "quote_info.loan_type",
    values: {
      Conventional: "conventional",
      "Homeready/Homepossible": "homeready",
      FHA: "fha",
      HELOC: "heloc",
      Reverse: "reverse",
      USDA: "usda",
      VA: "va",
      Jumbo: "jumbo",
      "Non QM": "non_qm",
      DPA: "dpa",
      Construction: "construction",
      Rehab: "rehab",
      Renovation: "renovation",
      default: "conventional"
    }
  },
  { incoming_key: "downPayment", key: "quote_info.down_payment" },
  { incoming_key: "cashOutAmount", key: "quote_info.cashout_amount" },
  {
    incoming_key: "currentMortgageBalance",
    key: "quote_info.current_mortgage_balance"
  },
  {
    incoming_key: "currentMortgageRate",
    key: "quote_info.current_mortgage_rate"
  }
]

// Generic incoming subscription mapping
export const GENERIC_INCOMING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = [
  { incoming_key: "first_name", key: "first_name" },
  { incoming_key: "last_name", key: "last_name" },
  { incoming_key: "email", key: "email" },
  { incoming_key: "phone", key: "phone" },
  { incoming_key: "loid", key: "loid" },
  {
    incoming_key: "loan_purpose",
    key: "quote_info.loan_purpose",
    values: {
      P: "purchase",
      R: "refinance",
      purchase: "purchase",
      refinance: "refinance",
      default: "purchase"
    }
  },
  {
    incoming_key: "loan_type",
    key: "quote_info.loan_type",
    values: {
      va: "va",
      usda: "usda",
      fha: "fha",
      conventional: "conventional",
      default: "conventional"
    }
  },
  { incoming_key: "loan_amount", key: "quote_info.loan_amount" },
  { incoming_key: "loan_term", key: "quote_info.loan_term" },
  { incoming_key: "list_price", key: "quote_info.list_price" },
  { incoming_key: "credit_score", key: "quote_info.credit_score" },
  {
    incoming_key: "property_type",
    key: "quote_info.property_type",
    values: {
      single: "single_family_home",
      single_family: "single_family_home",
      condominium: "condominium",
      condo: "condominium",
      townhome: "townhome",
      townhouse: "townhome",
      multifamily: "multi_tenant_home",
      multi_family: "multi_tenant_home",
      default: "single_family_home"
    }
  },
  {
    incoming_key: "residency_type",
    key: "quote_info.residency_type",
    values: {
      primary: "primary_home",
      primary_home: "primary_home",
      secondary: "second_home",
      second_home: "second_home",
      investment: "rental_home",
      rental: "rental_home",
      default: "primary_home"
    }
  },
  { incoming_key: "state", key: "quote_info.location.state" },
  { incoming_key: "city", key: "quote_info.location.city" },
  { incoming_key: "zipcode", key: "quote_info.location.zipcode" },
  { incoming_key: "zip", key: "quote_info.location.zipcode" },
  { incoming_key: "county", key: "quote_info.location.county" },
  { incoming_key: "street_address", key: "quote_info.location.street_address" }
]

// Relcu outgoing lead mapping
export const RELCU_OUTGOING_LEAD_MAPPING: ConnectorFieldMapping = [
  { key: "first_name", outgoing_key: "first_name" },
  { key: "last_name", outgoing_key: "last_name" },
  { key: "email", outgoing_key: "email" },
  { key: "phone", outgoing_key: "phone" },
  { key: "details.list_price", outgoing_key: "list_price" },
  { key: "details.credit_score", outgoing_key: "credit_score" },
  { key: "details.loan_amount", outgoing_key: "loan_amount" },
  { key: "details.loan_purpose", outgoing_key: "loan_purpose" },
  { key: "details.loan_type", outgoing_key: "loanProgram" },
  { key: "details.down_payment", outgoing_key: "downPayment" },
  { key: "details.property_type", outgoing_key: "property_type" },
  { key: "details.residency_type", outgoing_key: "residency_type" },
  { key: "details.location.city", outgoing_key: "city" },
  { key: "details.location.state", outgoing_key: "state" },
  { key: "details.location.zipcode", outgoing_key: "property_zip_code" }
]

export const RELCU_OUTGOING_LEAD_TEMPLATE = JSON.stringify(
  {
    first_name: "{{item.details.first_name}}",
    last_name: "{{item.details.last_name}}",
    email: "{{item.email}}",
    phone: "{{item.phone}}",
    list_price: "{{item.details.list_price}}",
    credit_score: "{{item.details.credit_score}}",
    loan_amount: "{{item.details.loan_amount}}",
    loan_purpose: "{{item.details.loan_purpose}}",
    loanProgram: "{{item.details.loan_type}}",
    downPayment: "{{item.details.down_payment}}",
    property_type: "{{item.details.property_type}}",
    residency_type: "{{item.details.residency_type}}",
    city: "{{item.details.location.city}}",
    state: "{{item.details.location.state}}",
    property_zip_code: "{{item.details.location.zipcode}}",
    leadAssignee: "{{lo.email}}"
  },
  null,
  2
)

// Relcu outgoing subscription mapping
export const RELCU_OUTGOING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = [
  { key: "first_name", outgoing_key: "first_name" },
  { key: "last_name", outgoing_key: "last_name" },
  { key: "email", outgoing_key: "email" },
  { key: "phone", outgoing_key: "phone" },
  { key: "quote_info.list_price", outgoing_key: "list_price" },
  { key: "quote_info.credit_score", outgoing_key: "credit_score" },
  { key: "quote_info.loan_amount", outgoing_key: "loan_amount" },
  { key: "quote_info.loan_purpose", outgoing_key: "loan_purpose" },
  { key: "quote_info.loan_type", outgoing_key: "loanProgram" },
  { key: "quote_info.down_payment", outgoing_key: "downPayment" },
  { key: "quote_info.property_type", outgoing_key: "property_type" },
  { key: "quote_info.residency_type", outgoing_key: "residency_type" },
  { key: "quote_info.location.city", outgoing_key: "city" },
  { key: "quote_info.location.state", outgoing_key: "state" },
  { key: "quote_info.location.zipcode", outgoing_key: "property_zip_code" }
]

export const RELCU_OUTGOING_SUBSCRIPTION_TEMPLATE = JSON.stringify(
  {
    first_name: "{{item.first_name}}",
    last_name: "{{item.last_name}}",
    email: "{{item.email}}",
    phone: "{{item.phone}}",
    list_price: "{{item.quote_info.list_price}}",
    credit_score: "{{item.quote_info.credit_score}}",
    loan_amount: "{{item.quote_info.loan_amount}}",
    loan_purpose: "{{item.quote_info.loan_purpose}}",
    loanProgram: "{{item.quote_info.loan_type}}",
    downPayment: "{{item.quote_info.down_payment}}",
    property_type: "{{item.quote_info.property_type}}",
    residency_type: "{{item.quote_info.residency_type}}",
    city: "{{item.quote_info.location.city}}",
    state: "{{item.quote_info.location.state}}",
    property_zip_code: "{{item.quote_info.location.zipcode}}",
    leadAssignee: "{{lo.email}}"
  },
  null,
  2
)

// Insellerate outgoing lead mapping (BB->Insellerate numeric codes)
export const INSELLERATE_OUTGOING_LEAD_MAPPING: ConnectorFieldMapping = [
  { key: "first_name", outgoing_key: "First_Name" },
  { key: "last_name", outgoing_key: "Last_Name" },
  { key: "email", outgoing_key: "Email" },
  { key: "phone", outgoing_key: "Phone" },
  { key: "details.location.city", outgoing_key: "City_Name" },
  { key: "details.location.state", outgoing_key: "State_Name" },
  { key: "details.location.zipcode", outgoing_key: "Zip_Code" },
  { key: "details.loan_amount", outgoing_key: "Loan1_Amount" },
  { key: "details.list_price", outgoing_key: "Home_Value" },
  { key: "details.credit_score", outgoing_key: "Credit_Score" },
  { key: "details.message", outgoing_key: "Notes" },
  {
    key: "details.military_eligible",
    outgoing_key: "Veteran",
    values: {
      yes: 1192,
      no: 1194,
      default: 1194
    }
  },
  {
    key: "details.residency_type",
    outgoing_key: "ResidenceTypeId",
    values: {
      primary_home: 1,
      second_home: 2,
      rental: 3,
      default: 1
    }
  },
  {
    key: "details.property_type",
    outgoing_key: "PropertyTypeId",
    values: {
      single_family_home: 10335,
      townhome: 10334,
      multi_tenant_home: 10334,
      condominium: 10336,
      default: 10335
    }
  },
  {
    key: "details.loan_purpose",
    outgoing_key: "Loan1_PurposeType_Proposed",
    values: {
      purchase: 1208,
      refinance: 1209,
      default: 1208
    }
  }
]

export const INSELLERATE_OUTGOING_LEAD_TEMPLATE = JSON.stringify(
  {
    root: {
      row: [
        {
          First_Name: "{{item.first_name}}",
          Last_Name: "{{item.last_name}}",
          Email: "{{item.email}}",
          Phone: "{{item.phone}}",
          City_Name: "{{item.details.location.city}}",
          State_Name: "{{item.details.location.state}}",
          Zip_Code: "{{item.details.location.zipcode}}",
          Loan1_Amount: "{{item.details.loan_amount}}",
          Home_Value: "{{item.details.list_price}}",
          Credit_Score: "{{item.details.credit_score}}",
          Notes: "{{item.details.message}}",
          Veteran: "{{@transform(item.details.military_eligible)}}",
          ResidenceTypeId: "{{@transform(item.details.residency_type)}}",
          PropertyTypeId: "{{@transform(item.details.property_type)}}",
          Loan1_PurposeType_Proposed:
            "{{@transform(item.details.loan_purpose)}}",
          AssignToEmail: "{{lo.email}}",
          Loan1_LienPosition: "",
          Loan1_InterestRate: "",
          Loan1_RateType_Proposed: "",
          IncomeDocType: "",
          Loan1_CurrentMortgageBalance: "",
          PropertyId: ""
        }
      ]
    }
  },
  null,
  2
)

// Insellerate outgoing subscription mapping (BB->Insellerate numeric codes)
export const INSELLERATE_OUTGOING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping =
  [
    { key: "first_name", outgoing_key: "First_Name" },
    { key: "last_name", outgoing_key: "Last_Name" },
    { key: "email", outgoing_key: "Email" },
    { key: "phone", outgoing_key: "Phone" },
    { key: "quote_info.location.city", outgoing_key: "City_Name" },
    { key: "quote_info.location.state", outgoing_key: "State_Name" },
    { key: "quote_info.location.zipcode", outgoing_key: "Zip_Code" },
    { key: "quote_info.loan_amount", outgoing_key: "Loan1_Amount" },
    { key: "quote_info.list_price", outgoing_key: "Home_Value" },
    { key: "quote_info.credit_score", outgoing_key: "Credit_Score" },
    {
      key: "quote_info.military_eligible",
      outgoing_key: "Veteran",
      values: {
        yes: 1192,
        no: 1194,
        default: 1194
      }
    },
    {
      key: "quote_info.residency_type",
      outgoing_key: "ResidenceTypeId",
      values: {
        primary_home: 1,
        second_home: 2,
        rental: 3,
        default: 1
      }
    },
    {
      key: "quote_info.property_type",
      outgoing_key: "PropertyTypeId",
      values: {
        single_family_home: 10335,
        townhome: 10334,
        multi_tenant_home: 10334,
        condominium: 10336,
        default: 10335
      }
    },
    {
      key: "quote_info.loan_purpose",
      outgoing_key: "Loan1_PurposeType_Proposed",
      values: {
        purchase: 1208,
        refinance: 1209,
        default: 1208
      }
    }
  ]

export const INSELLERATE_OUTGOING_SUBSCRIPTION_TEMPLATE = JSON.stringify(
  {
    root: {
      row: [
        {
          First_Name: "{{item.first_name}}",
          Last_Name: "{{item.last_name}}",
          Email: "{{item.email}}",
          Phone: "{{item.phone}}",
          City_Name: "{{item.quote_info.location.city}}",
          State_Name: "{{item.quote_info.location.state}}",
          Zip_Code: "{{item.quote_info.location.zipcode}}",
          Loan1_Amount: "{{item.quote_info.loan_amount}}",
          Home_Value: "{{item.quote_info.list_price}}",
          Credit_Score: "{{item.quote_info.credit_score}}",
          Veteran: "{{@transform(item.quote_info.military_eligible)}}",
          ResidenceTypeId: "{{@transform(item.quote_info.residency_type)}}",
          PropertyTypeId: "{{@transform(item.quote_info.property_type)}}",
          Loan1_PurposeType_Proposed:
            "{{@transform(item.quote_info.loan_purpose)}}",
          AssignToEmail: "{{lo.email}}",
          Loan1_LienPosition: "",
          Loan1_InterestRate: "",
          Loan1_RateType_Proposed: "",
          IncomeDocType: "",
          Loan1_CurrentMortgageBalance: "",
          PropertyId: ""
        }
      ]
    }
  },
  null,
  2
)

// Bonzo outgoing lead mapping (straight pass-through, no transforms)
export const BONZO_OUTGOING_LEAD_MAPPING: ConnectorFieldMapping = [
  { key: "first_name", outgoing_key: "first_name" },
  { key: "last_name", outgoing_key: "last_name" },
  { key: "email", outgoing_key: "email" },
  { key: "phone", outgoing_key: "phone" },
  { key: "details.loan_amount", outgoing_key: "loan_amount" },
  { key: "details.down_payment", outgoing_key: "down_payment" },
  { key: "details.credit_score", outgoing_key: "credit_score" },
  { key: "details.loan_purpose", outgoing_key: "loan_type" },
  { key: "details.property_type", outgoing_key: "property_type" },
  { key: "details.residency_type", outgoing_key: "property_use" },
  { key: "details.list_price", outgoing_key: "property_value" },
  { key: "details.location.city", outgoing_key: "property_city" },
  { key: "details.location.state", outgoing_key: "property_state" },
  { key: "details.location.zipcode", outgoing_key: "property_zip" },
  { key: "details.foreclosure_bankruptcy", outgoing_key: "bankruptcy" },
  { key: "details.military_eligible", outgoing_key: "military_status" },
  { key: "details.household_income", outgoing_key: "household_income" },
  { key: "details.purchase_readiness", outgoing_key: "current_step" }
]

export const BONZO_OUTGOING_LEAD_TEMPLATE = JSON.stringify(
  {
    first_name: "{{item.first_name}}",
    last_name: "{{item.last_name}}",
    email: "{{item.email}}",
    phone: "{{item.phone}}",
    lead_id: "https://cloud.bankingbridge.com/leads/{{item.id}}",
    loan_amount: "{{item.details.loan_amount}}",
    down_payment: "{{item.details.down_payment}}",
    credit_score: "{{item.details.credit_score}}",
    loan_type: "{{item.details.loan_purpose}}",
    property_type: "{{item.details.property_type}}",
    property_use: "{{item.details.residency_type}}",
    property_value: "{{item.details.list_price}}",
    property_city: "{{item.details.location.city}}",
    property_state: "{{item.details.location.state}}",
    property_zip: "{{item.details.location.zipcode}}",
    bankruptcy: "{{item.details.foreclosure_bankruptcy}}",
    military_status: "{{item.details.military_eligible}}",
    household_income: "{{item.details.household_income}}",
    current_step: "{{item.details.purchase_readiness}}",
    lead_source: "BankingBridge"
  },
  null,
  2
)

// Bonzo outgoing subscription mapping (straight pass-through, no transforms)
export const BONZO_OUTGOING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = [
  { key: "first_name", outgoing_key: "first_name" },
  { key: "last_name", outgoing_key: "last_name" },
  { key: "email", outgoing_key: "email" },
  { key: "phone", outgoing_key: "phone" },
  { key: "quote_info.loan_amount", outgoing_key: "loan_amount" },
  { key: "quote_info.down_payment", outgoing_key: "down_payment" },
  { key: "quote_info.credit_score", outgoing_key: "credit_score" },
  { key: "quote_info.loan_purpose", outgoing_key: "loan_type" },
  { key: "quote_info.property_type", outgoing_key: "property_type" },
  { key: "quote_info.residency_type", outgoing_key: "property_use" },
  { key: "quote_info.list_price", outgoing_key: "property_value" },
  { key: "quote_info.location.city", outgoing_key: "property_city" },
  { key: "quote_info.location.state", outgoing_key: "property_state" },
  { key: "quote_info.location.zipcode", outgoing_key: "property_zip" }
]

export const BONZO_OUTGOING_SUBSCRIPTION_TEMPLATE = JSON.stringify(
  {
    first_name: "{{item.first_name}}",
    last_name: "{{item.last_name}}",
    email: "{{item.email}}",
    phone: "{{item.phone}}",
    lead_id: "https://cloud.bankingbridge.com/leads/{{item.id}}",
    loan_amount: "{{item.quote_info.loan_amount}}",
    down_payment: "{{item.quote_info.down_payment}}",
    credit_score: "{{item.quote_info.credit_score}}",
    loan_type: "{{item.quote_info.loan_purpose}}",
    property_type: "{{item.quote_info.property_type}}",
    property_use: "{{item.quote_info.residency_type}}",
    property_value: "{{item.quote_info.list_price}}",
    property_city: "{{item.quote_info.location.city}}",
    property_state: "{{item.quote_info.location.state}}",
    property_zip: "{{item.quote_info.location.zipcode}}",
    lead_source: "BankingBridge"
  },
  null,
  2
)

// Shape outgoing lead mapping (form-encoded, property type transform)
export const SHAPE_OUTGOING_LEAD_MAPPING: ConnectorFieldMapping = [
  { key: "first_name", outgoing_key: "firstname" },
  { key: "last_name", outgoing_key: "lastname" },
  { key: "email", outgoing_key: "email" },
  { key: "phone", outgoing_key: "phone" },
  { key: "details.credit_score", outgoing_key: "creditscore" },
  { key: "details.loan_amount", outgoing_key: "LoanAmount" },
  { key: "details.list_price", outgoing_key: "borpurchasePrice" },
  { key: "details.down_payment", outgoing_key: "downpmtamount2" },
  { key: "details.location.city", outgoing_key: "prCity" },
  { key: "details.location.state", outgoing_key: "prState" },
  { key: "details.location.zipcode", outgoing_key: "prZipCode" },
  { key: "details.cashout_amount", outgoing_key: "loncashOutAmt" },
  { key: "details.loan_purpose", outgoing_key: "lonpurpose" },
  { key: "details.loan_type", outgoing_key: "lonloantype2" },
  { key: "details.loan_purpose", outgoing_key: "qkapppurpose" },
  {
    key: "details.property_type",
    outgoing_key: "qkapppropertyType",
    values: {
      single_family_home: "Single Family",
      multi_tenant_home: "Multi Family",
      townhome: "Townhouse",
      condominium: "Condo",
      default: "Single Family"
    }
  }
]

export const SHAPE_OUTGOING_LEAD_TEMPLATE = JSON.stringify(
  {
    firstname: "{{item.first_name}}",
    lastname: "{{item.last_name}}",
    email: "{{item.email}}",
    phone: "{{item.phone}}",
    creditscore: "{{item.details.credit_score}}",
    LoanAmount: "{{item.details.loan_amount}}",
    borpurchasePrice: "{{item.details.list_price}}",
    downpmtamount2: "{{item.details.down_payment}}",
    prCity: "{{item.details.location.city}}",
    prState: "{{item.details.location.state}}",
    prZipCode: "{{item.details.location.zipcode}}",
    loncashOutAmt: "{{item.details.cashout_amount}}",
    lonpurpose: "{{item.details.loan_purpose}}",
    lonloantype2: "{{item.details.loan_type}}",
    qkapppurpose: "{{item.details.loan_purpose}}",
    qkapppropertyType: "{{@transform(item.details.property_type)}}",
    leadid: "{{item.id}}",
    leadreferenceid: "https://cloud.bankingbridge.com/leads/{{item.id}}",
    leadfullname: "{{item.first_name}} {{item.last_name}}"
  },
  null,
  2
)

// Shape outgoing subscription mapping (form-encoded, property type transform)
export const SHAPE_OUTGOING_SUBSCRIPTION_MAPPING: ConnectorFieldMapping = [
  { key: "first_name", outgoing_key: "firstname" },
  { key: "last_name", outgoing_key: "lastname" },
  { key: "email", outgoing_key: "email" },
  { key: "phone", outgoing_key: "phone" },
  { key: "quote_info.credit_score", outgoing_key: "creditscore" },
  { key: "quote_info.loan_amount", outgoing_key: "LoanAmount" },
  { key: "quote_info.list_price", outgoing_key: "borpurchasePrice" },
  { key: "quote_info.down_payment", outgoing_key: "downpmtamount2" },
  { key: "quote_info.location.city", outgoing_key: "prCity" },
  { key: "quote_info.location.state", outgoing_key: "prState" },
  { key: "quote_info.location.zipcode", outgoing_key: "prZipCode" },
  { key: "quote_info.cashout_amount", outgoing_key: "loncashOutAmt" },
  { key: "quote_info.loan_purpose", outgoing_key: "lonpurpose" },
  { key: "quote_info.loan_type", outgoing_key: "lonloantype2" },
  { key: "quote_info.loan_purpose", outgoing_key: "qkapppurpose" },
  {
    key: "quote_info.property_type",
    outgoing_key: "qkapppropertyType",
    values: {
      single_family_home: "Single Family",
      multi_tenant_home: "Multi Family",
      townhome: "Townhouse",
      condominium: "Condo",
      default: "Single Family"
    }
  }
]

export const SHAPE_OUTGOING_SUBSCRIPTION_TEMPLATE = JSON.stringify(
  {
    firstname: "{{item.first_name}}",
    lastname: "{{item.last_name}}",
    email: "{{item.email}}",
    phone: "{{item.phone}}",
    creditscore: "{{item.quote_info.credit_score}}",
    LoanAmount: "{{item.quote_info.loan_amount}}",
    borpurchasePrice: "{{item.quote_info.list_price}}",
    downpmtamount2: "{{item.quote_info.down_payment}}",
    prCity: "{{item.quote_info.location.city}}",
    prState: "{{item.quote_info.location.state}}",
    prZipCode: "{{item.quote_info.location.zipcode}}",
    loncashOutAmt: "{{item.quote_info.cashout_amount}}",
    lonpurpose: "{{item.quote_info.loan_purpose}}",
    lonloantype2: "{{item.quote_info.loan_type}}",
    qkapppurpose: "{{item.quote_info.loan_purpose}}",
    qkapppropertyType: "{{@transform(item.quote_info.property_type)}}",
    leadid: "{{item.id}}",
    leadreferenceid: "https://cloud.bankingbridge.com/leads/{{item.id}}",
    leadfullname: "{{item.first_name}} {{item.last_name}}"
  },
  null,
  2
)

// Template definitions
export const CONNECTOR_TEMPLATES: ConnectorTemplate[] = [
  {
    name: "Zapier - Lead (Standard)",
    type: "zapier",
    data_type: "lead",
    field_mapping: ZAPIER_LEAD_MAPPING,
    description:
      "Standard field mapping for Zapier lead webhooks with camelCase field names"
  },
  {
    name: "Generic Webhook - Lead (Lowercase)",
    type: "webhook",
    data_type: "lead",
    field_mapping: GENERIC_LEAD_MAPPING,
    description:
      "Generic webhook mapping for leads with lowercase, no-underscore field names (common in many CRMs)"
  },
  {
    name: "Own Up - Incoming Lead",
    type: "webhook",
    data_type: "lead",
    field_mapping: OWNUP_INCOMING_LEAD_MAPPING,
    description:
      "Incoming lead mapping for Own Up mortgage marketplace with comprehensive borrower and property data"
  },
  {
    name: "MRC - Incoming Lead",
    type: "webhook",
    data_type: "lead",
    field_mapping: MRC_INCOMING_LEAD_MAPPING,
    description:
      "Incoming lead mapping for MRC (Mortgage Research Center) with comprehensive borrower, property, and refinance data"
  },
  {
    name: "Bankrate - Incoming Lead",
    type: "webhook",
    data_type: "lead",
    field_mapping: BANKRATE_INCOMING_LEAD_MAPPING,
    description:
      "Incoming lead mapping for Bankrate with purchase and refinance fields"
  },
  {
    name: "LendingTree - Incoming Lead",
    type: "webhook",
    data_type: "lead",
    field_mapping: LENDINGTREE_INCOMING_LEAD_MAPPING,
    description:
      "Incoming lead mapping for LendingTree with support for purchase, refinance, and home equity verticals"
  },
  {
    name: "Bankrate - Incoming Subscription",
    type: "webhook",
    data_type: "subscription",
    field_mapping: BANKRATE_INCOMING_SUBSCRIPTION_MAPPING,
    description:
      "Incoming subscription mapping for Bankrate with purchase and refinance fields"
  },
  {
    name: "Zillow - Incoming Lead",
    type: "webhook",
    data_type: "lead",
    field_mapping: ZILLOW_INCOMING_LEAD_MAPPING,
    description:
      "Incoming lead mapping for Zillow Lender Contact Notification API with comprehensive borrower, property, and loan data"
  },
  {
    name: "Zillow - Incoming Subscription",
    type: "webhook",
    data_type: "subscription",
    field_mapping: ZILLOW_INCOMING_SUBSCRIPTION_MAPPING,
    description:
      "Incoming subscription mapping for Zillow with property and loan quote data"
  },
  {
    name: "LendingTree - Incoming Subscription",
    type: "webhook",
    data_type: "subscription",
    field_mapping: LENDINGTREE_INCOMING_SUBSCRIPTION_MAPPING,
    description:
      "Incoming subscription mapping for LendingTree with support for purchase, refinance, and home equity verticals"
  },
  {
    name: "Zapier - Subscription",
    type: "zapier",
    data_type: "subscription",
    field_mapping: ZAPIER_SUBSCRIPTION_MAPPING,
    description: "Standard field mapping for Zapier subscription webhooks"
  },
  {
    name: "Generic - Incoming Subscription",
    type: "webhook",
    data_type: "subscription",
    field_mapping: GENERIC_INCOMING_SUBSCRIPTION_MAPPING,
    description:
      "Generic incoming subscription mapping with value transformations for common field formats"
  },
  {
    name: "Own Up - Incoming Subscription",
    type: "webhook",
    data_type: "subscription",
    field_mapping: OWNUP_INCOMING_SUBSCRIPTION_MAPPING,
    description:
      "Incoming subscription mapping for Own Up mortgage marketplace with property and loan data"
  },
  {
    name: "MRC - Incoming Subscription",
    type: "webhook",
    data_type: "subscription",
    field_mapping: MRC_INCOMING_SUBSCRIPTION_MAPPING,
    description:
      "Incoming subscription mapping for MRC (Mortgage Research Center) with property and loan data"
  },
  {
    name: "Zapier - Pending Subscription",
    type: "zapier",
    data_type: "pending_subscription",
    field_mapping: ZAPIER_PENDING_SUBSCRIPTION_MAPPING,
    description:
      "Standard field mapping for Zapier pending subscription webhooks"
  },
  {
    name: "Relcu - Outgoing Lead",
    type: "webhook",
    data_type: "lead",
    direction: "outgoing",
    content_type: "application/x-www-form-urlencoded",
    field_mapping: RELCU_OUTGOING_LEAD_MAPPING,
    template: RELCU_OUTGOING_LEAD_TEMPLATE,
    description:
      "Outgoing lead mapping for Relcu CRM partner import API with form-encoded data"
  },
  {
    name: "Relcu - Outgoing Subscription",
    type: "webhook",
    data_type: "subscription",
    direction: "outgoing",
    content_type: "application/x-www-form-urlencoded",
    field_mapping: RELCU_OUTGOING_SUBSCRIPTION_MAPPING,
    template: RELCU_OUTGOING_SUBSCRIPTION_TEMPLATE,
    description:
      "Outgoing subscription mapping for Relcu CRM partner import API with form-encoded data"
  },
  {
    name: "Insellerate - Outgoing Lead",
    type: "webhook",
    data_type: "lead",
    direction: "outgoing",
    content_type: "application/json",
    field_mapping: INSELLERATE_OUTGOING_LEAD_MAPPING,
    template: INSELLERATE_OUTGOING_LEAD_TEMPLATE,
    description:
      "Outgoing lead mapping for Insellerate CRM with numeric code value transforms for veteran status, property type, residency type, and loan purpose"
  },
  {
    name: "Insellerate - Outgoing Subscription",
    type: "webhook",
    data_type: "subscription",
    direction: "outgoing",
    content_type: "application/json",
    field_mapping: INSELLERATE_OUTGOING_SUBSCRIPTION_MAPPING,
    template: INSELLERATE_OUTGOING_SUBSCRIPTION_TEMPLATE,
    description:
      "Outgoing subscription mapping for Insellerate CRM with numeric code value transforms"
  },
  {
    name: "Bonzo - Outgoing Lead",
    type: "webhook",
    data_type: "lead",
    direction: "outgoing",
    content_type: "application/json",
    field_mapping: BONZO_OUTGOING_LEAD_MAPPING,
    template: BONZO_OUTGOING_LEAD_TEMPLATE,
    description:
      "Outgoing lead mapping for Bonzo CRM with flat JSON payload and BB lead link"
  },
  {
    name: "Bonzo - Outgoing Subscription",
    type: "webhook",
    data_type: "subscription",
    direction: "outgoing",
    content_type: "application/json",
    field_mapping: BONZO_OUTGOING_SUBSCRIPTION_MAPPING,
    template: BONZO_OUTGOING_SUBSCRIPTION_TEMPLATE,
    description:
      "Outgoing subscription mapping for Bonzo CRM with flat JSON payload and BB lead link"
  },
  {
    name: "Shape - Outgoing Lead",
    type: "webhook",
    data_type: "lead",
    direction: "outgoing",
    content_type: "application/x-www-form-urlencoded",
    field_mapping: SHAPE_OUTGOING_LEAD_MAPPING,
    template: SHAPE_OUTGOING_LEAD_TEMPLATE,
    description:
      "Outgoing lead mapping for Shape CRM with form-encoded data and property type transforms"
  },
  {
    name: "Shape - Outgoing Subscription",
    type: "webhook",
    data_type: "subscription",
    direction: "outgoing",
    content_type: "application/x-www-form-urlencoded",
    field_mapping: SHAPE_OUTGOING_SUBSCRIPTION_MAPPING,
    template: SHAPE_OUTGOING_SUBSCRIPTION_TEMPLATE,
    description:
      "Outgoing subscription mapping for Shape CRM with form-encoded data and property type transforms"
  }
]

/**
 * Get available templates filtered by data type
 */
export const getTemplatesByDataType = (
  dataType: "lead" | "subscription" | "pending_subscription",
  direction?: ConnectorDirection
): ConnectorTemplate[] => {
  return CONNECTOR_TEMPLATES.filter(t => {
    if (t.data_type !== dataType) return false
    if (direction === "outgoing" && t.direction !== "outgoing") return false
    if (direction === "incoming" && t.direction === "outgoing") return false
    return true
  })
}

/**
 * Get template by name
 */
export const getTemplateByName = (
  name: string
): ConnectorTemplate | undefined => {
  return CONNECTOR_TEMPLATES.find(t => t.name === name)
}

/**
 * Get default template for a given CRM type and data type
 */
export const getDefaultTemplate = (
  crmType: string,
  dataType: "lead" | "subscription" | "pending_subscription"
): ConnectorTemplate | undefined => {
  return CONNECTOR_TEMPLATES.find(
    t => t.type === crmType && t.data_type === dataType
  )
}

// Field reference documentation for help text
export const LEAD_FIELD_OPTIONS = [
  { value: "email", label: "Email", category: "Basic" },
  { value: "phone", label: "Phone", category: "Basic" },
  { value: "first_name", label: "First Name", category: "Basic" },
  { value: "last_name", label: "Last Name", category: "Basic" },
  {
    value: "details.loan_purpose",
    label: "Loan Purpose",
    category: "Loan Info"
  },
  { value: "details.loan_type", label: "Loan Type", category: "Loan Info" },
  { value: "details.loan_amount", label: "Loan Amount", category: "Loan Info" },
  { value: "details.loan_term", label: "Loan Term", category: "Loan Info" },
  {
    value: "details.credit_score",
    label: "Credit Score",
    category: "Loan Info"
  },
  {
    value: "details.down_payment",
    label: "Down Payment",
    category: "Loan Info"
  },
  { value: "details.list_price", label: "List Price", category: "Loan Info" },
  {
    value: "details.property_type",
    label: "Property Type",
    category: "Property"
  },
  {
    value: "details.residency_type",
    label: "Residency Type",
    category: "Property"
  },
  { value: "details.realtor", label: "Realtor", category: "Property" },
  { value: "details.location.city", label: "City", category: "Location" },
  { value: "details.location.state", label: "State", category: "Location" },
  {
    value: "details.location.zipcode",
    label: "Zip Code",
    category: "Location"
  },
  {
    value: "details.location.street_address",
    label: "Street Address",
    category: "Location"
  },
  { value: "details.location.county", label: "County", category: "Location" },
  { value: "details.message", label: "Message", category: "Other" },
  { value: "dashboard_url", label: "Dashboard URL", category: "Other" },
  { value: "dashboard_token", label: "Dashboard Token", category: "Other" },
  { value: "app_key", label: "App Key", category: "Other" }
]

export const SUBSCRIPTION_FIELD_OPTIONS = [
  { value: "email", label: "Email", category: "Basic" },
  { value: "phone", label: "Phone", category: "Basic" },
  { value: "first_name", label: "First Name", category: "Basic" },
  { value: "last_name", label: "Last Name", category: "Basic" },
  { value: "loid", label: "Loan Officer ID", category: "Basic" },
  { value: "type", label: "Type", category: "Basic" },
  { value: "active", label: "Active", category: "Basic" },
  {
    value: "quote_info.loan_purpose",
    label: "Loan Purpose",
    category: "Quote Info"
  },
  {
    value: "quote_info.credit_score",
    label: "Credit Score",
    category: "Quote Info"
  },
  {
    value: "quote_info.property_type",
    label: "Property Type",
    category: "Quote Info"
  },
  {
    value: "quote_info.residency_type",
    label: "Residency Type",
    category: "Quote Info"
  },
  {
    value: "quote_info.list_price",
    label: "List Price",
    category: "Quote Info"
  },
  {
    value: "quote_info.loan_amount",
    label: "Loan Amount",
    category: "Quote Info"
  },
  {
    value: "quote_info.loan_term",
    label: "Loan Term",
    category: "Quote Info"
  },
  {
    value: "quote_info.loan_type",
    label: "Loan Type",
    category: "Quote Info"
  },
  {
    value: "quote_info.location.state",
    label: "State",
    category: "Location"
  },
  { value: "quote_info.location.city", label: "City", category: "Location" },
  {
    value: "quote_info.location.zipcode",
    label: "Zip Code",
    category: "Location"
  },
  {
    value: "quote_info.location.street_address",
    label: "Street Address",
    category: "Location"
  },
  {
    value: "quote_info.location.county",
    label: "County",
    category: "Location"
  },
  { value: "dashboard_url", label: "Dashboard URL", category: "Other" },
  { value: "dashboard_token", label: "Dashboard Token", category: "Other" }
]

export const PENDING_SUBSCRIPTION_FIELD_OPTIONS = [
  ...SUBSCRIPTION_FIELD_OPTIONS,
  { value: "trigger_rate", label: "Trigger Rate", category: "Basic" }
]
